import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { BehaviorSubject, of, Observable, Subscription, from } from "rxjs";
import { mergeMap, map, switchMap, tap } from "rxjs/operators";
import { PaymentItem, ApplicationPayment, Payment, PaymentStatus, PaymentSearchCriteria, AuthorizationResponse } from "../../../../payments/models/paymentItem.model";
import { PaymentService } from "../../../../payments/services/payment.service";
import { DocumentConfig } from "../../../../services/application-config.service";




@Component({
    selector: 'invoice',
    templateUrl: './invoice.component.html',
    styleUrls: ['./invoice.component.scss'],
    standalone: false
})
export class InvoiceComponent implements OnInit, OnDestroy {

    get applicationNumber(): string {
        if (this.fees.length == 0) return "";
        return this.fees[0].applicationNumber;
    }

    private _authorization: AuthorizationResponse;

    get authorization(): AuthorizationResponse {
        return this._authorization;
    }
    
    @Input() set authorization(value: AuthorizationResponse) {
        this._authorization = value;
    }

    cancellationText: string;

    private _config: DocumentConfig;

    get config(): DocumentConfig {
        return this._config;
    }

    @Input() set config(value: DocumentConfig) {
        this._config = value;
    }

    get isCredited(): boolean {
        return this.creditedTypes.includes(this.paymentType);
    }

    creditedTypes = ["Credit", "Debit", "Check"];


    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    private _documentId: any;

    get documentId(): any {
        return this._documentId;
    }

    @Input() set documentId(value: any) {
        this._documentId = value;
    }

    private _fees: ApplicationPayment[];

    get fees(): ApplicationPayment[] {
        return this._fees;
    }

    @Input() set fees(value: ApplicationPayment[]) {
        this._fees = value;
    }

    get generatePdf(): boolean {
        return this.documentId == null;
    }

    get isError(): boolean {
        if (this.authorization == null || this.authorization.Error == null) return false;
        return this.authorization.Error == "True" || this.authorization.Error == "true";
    }


    get isLiquidAsset(): boolean {
        return this.liquidTypes.includes(this.paymentType);
    }

    liquidTypes = ["LocalCheck"];

    loadingInvoice$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    @Output() newResponseReceived: EventEmitter<AuthorizationResponse> = new EventEmitter<AuthorizationResponse>();

    @Input() paymentType: string;

    get receiptConfig(): any {
        let conf = {
            invoiceType: {
                name: ""
            },
            receiptType: {
                name: ""
            }
        }
        if (this.config != null) {
            conf.invoiceType.name = this.config.invoice;
            conf.receiptType.name = this.config.receipt;
        }
        return conf;
    }

    @Output() receiptIssued: EventEmitter<ApplicationPayment[]> = new EventEmitter<ApplicationPayment[]>();

    subscription: Subscription = new Subscription();

    constructor(private svc: PaymentService, private route: ActivatedRoute) { }

    ngOnInit() {
        let obs$: Observable<ApplicationPayment[]> = (this.fees != null && this.fees.length > 0)
            ? of(this.fees)
            : (this.applicationNumber != null && this.applicationNumber.length > 0)
                ? this.svc.search(new PaymentSearchCriteria("", this.applicationNumber, []))
                : of([]);
        const search$ = obs$.pipe(
            mergeMap((results: ApplicationPayment[]) => {
                for (let p = 0; p < results.length; p++) {
                    if (results[p].invoiceDocumentType == null || results[p].invoiceDocumentType != this.config.invoice) results[p].invoiceDocumentType = this.config.invoice;
                    if (results[p].receiptDocumentType == null || results[p].receiptDocumentType != this.config.receipt) results[p].receiptDocumentType = this.config.receipt;
                }
                this.fees = results;
                if (this.isCredited) {
                    let responses: AuthorizationResponse[] = [];                   
                    //there ought to be only one response here, but due to data structure restrictions have to treat it as a collection
                    let respObs$: Observable<AuthorizationResponse[]> = (responses.length == 0)
                        ? this.route.queryParams.pipe(
                            map((params) => { return params["id"]; }),
                            switchMap((id: string) => {
                                if (id != null) {
                                    return from(this.svc.detokenizeResponse(id)).pipe(
                                        map((response: any) => {
                                            let conv = JSON.parse(response as any) as AuthorizationResponse;
                                            responses.push(conv);
                                            let hasError = responses.filter(r => r.Error == "True");
                                            for (let e = 0; e < hasError.length; e++) {
                                                if (this.cancellationText.length == 0) this.cancellationText = "Payment Status: ";
                                                else this.cancellationText = `${this.cancellationText},`;
                                                this.cancellationText = `${this.cancellationText} ${hasError[e].Error}`;
                                            }
                                            this.newResponseReceived.emit(conv);
                                            return responses;
                                        })
                                    )
                                } else {
                                    return of([this._authorization]);
                                }
                            })
                        )
                        : of(responses);
                    return respObs$.pipe(
                        tap((data: AuthorizationResponse[]) => {
                            this.authorization = data[0];
                        })
                    );
                } else {
                    this.dataLoading.next(false);
                    this.loadingInvoice$.next(false);
                    return of(null);
                }
            })

        );

        let subs$ = search$.subscribe(
            () => { this.dataLoading.next(false); },
            (error) => {
                console.error(error);
                this.dataLoading.next(false);
            }
        );
        this.subscription.add(subs$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }


    onReceiptIssued(payments: ApplicationPayment[]): void {
        this.receiptIssued.emit(payments);
    }

}
